import { Injectable } from '@angular/core';
import { shareReplay, map, switchAll, tap } from 'rxjs/operators';
import { Observable, forkJoin, of } from 'rxjs';
import { TimeSeries, UTCDate } from 'map-wald';
import { Point, FeatureCollection, Feature } from 'geojson';
import { FeatureDataService, FeatureDataConfig } from './feature-data.service';
import { LayersService } from './layers.service';
import { DataMode, FlattenedLayerDescriptor, LEGEND_UNITS, LayerDescriptor, TimePeriod, getAggregation } from './data';
import { DataModeUtils, TimePeriodUtils } from './utils';
import { HttpClient } from '@angular/common/http';

const standardVariables = [
  'longitude',
  'latitude',
  'ID',
  'admin_country',
  'admin_province',
  'hydro_basin',
  'hydro_cat'
];

const KEEP_NULLS = false;

interface DatedCoverage {
  date: UTCDate;
  coverage: FeatureCollection<Point,any>;
}

@Injectable({
  providedIn: 'root'
})
export class PointDataService {
  private layers: Observable<LayerDescriptor[]>;
  private statsCache:{[key:string]:Observable<number[]>} = {};
  constructor(private layersService:LayersService,
              private featureData:FeatureDataService,
              private http:HttpClient) {
    this.layers = this.layersService.matchingLayers({type:'point'}).pipe(shareReplay());

    // http.get(`${environment.pointConfig}?${(new Date()).getTime()}`).pipe(
    //   tap((cfg:FeatureDataConfig[])=>{
    //     return cfg.forEach(lyr=>{
    //       lyr.meta = [].concat(standardVariables,lyr.meta||[])
    //     });
    //   }),
    //   shareReplay());
  }

  getLayers(): Observable<FeatureDataConfig[]> {
    return this.layers;
  }

  getSites(layer: string,filter?:{[key:string]:any}): Observable<FeatureCollection> {
    return this._layerConfig(layer).pipe(
      map(lyr=>this.featureData.getFeatures(lyr,filter)),
      switchAll());
  }

  getTimes(layer: string): Observable<UTCDate[]> {
    return this._layerConfig(layer).pipe(
      map(lyr=>this.featureData.getTimes(lyr)),
      switchAll());
  }

  _computeRelative(val:number,properties:{[key:string]:any},mode:string):number{
    let prop: string;
    if(mode.includes('[')){
      prop = mode.split('[')[0];
    } else {
      prop = mode;
    }

    let comp = properties[prop];
    if(comp&&mode.includes('[')){
      const idx = +(mode.split('[')[1].split(']')[0]);
      comp = comp[idx];
    }
    return val - comp;
  }

  _bin(val:number,bin:number[]):number{
    if(!bin?.findIndex){
      return NaN;
    }

    const idx = bin.findIndex(b=>val<=b);
    if(idx>=0){
      return idx;
    } else {
      return bin.length;
    }
  }

  getValues(layer:FlattenedLayerDescriptor,
            filter:{[key:string]:any},
            timeperiod:TimePeriod,
            dataMode:DataMode,
            variable?: string): Observable<FeatureCollection<Point>>{
    let result$ = of(layer).pipe(
      map(lyr=>forkJoin([of(lyr),this.featureData.getAggregateValues(lyr,filter,timeperiod,variable,true,getAggregation(layer))])),
      switchAll(),
      map(([layer,coverage])=>{
        return {
          layer,
          coverage
        };
      }),
      tap(data=>{
        capData(data.coverage,0.0,undefined)

        if(!data.coverage){
          return;
        }
        if(layer.relative){
          data.coverage.features.forEach(f=>{
            f.properties.value = this._computeRelative(f.properties.value,f.properties,layer.relative?.variable);
          });
        } else if(layer.bin){
          data.coverage.features.forEach(f=>{
            f.properties.value = this._bin(f.properties.value,f.properties[layer.bin]);
          });
        }

        configureFeatureUnits(data.coverage,data.layer,dataMode);
      }),
      map(data=>data.coverage),
      tap(coverage=>computeLimitRanks(coverage,layer)),
      map(coverage=>this.computeDataMode(coverage,layer,timeperiod,dataMode)),
      switchAll(),
    );

    if(!KEEP_NULLS){
      result$ = result$.pipe(map(filterNulls));
    }
    return result$;
  }

  computeDataMode(coverage: FeatureCollection<Point, { [name: string]: any; }>,
                  layer: FlattenedLayerDescriptor,
                  timeperiod: TimePeriod,
                  dataMode: DataMode): Observable<FeatureCollection<Point, { [name: string]: any; }>> {
    if(dataMode===DataMode.NORMAL){
      return of(coverage);
    }
    const wrapped:DatedCoverage[] = [
      {
        date: timeperiod.from,
        coverage
      }
    ]

    return forkJoin(['mean','std','trend'].map(v=>this.getSummaryStats(v,layer,timeperiod))).pipe(
      map(([mean,std,trend])=>({mean,std,trend})),
      map(stats=>{
        return DataModeUtils.processData(dataMode,timeperiod,getAggregation(layer),wrapped,{
          recurringTimePeriod:(data:DatedCoverage[],period:TimePeriod)=>{
            return data[0];
          },
          sum:(data:DatedCoverage[])=>{
            throw new Error('Not implemented');
          },
          average:(data:DatedCoverage[])=>copyAssign(data[0],stats.mean),
          subtract:(lhs:DatedCoverage,rhs:DatedCoverage)=>{
            const newValues = lhs.coverage.features.map((f,i)=>f.properties.value-rhs.coverage.features[i].properties.value);
            return copyAssign(lhs,newValues);
          },
          scale:(data:DatedCoverage,factor:number)=>{
            const newValues = data.coverage.features.map(f=>f.properties.value*factor);
            return copyAssign(data,newValues);
          },
          divide:(lhs:DatedCoverage,rhs:DatedCoverage)=>{
            const newValues = rhs.coverage.features.map((f,i)=>f.properties.value?lhs.coverage.features[i].properties.value/rhs.coverage.features[i].properties.value:0.0);
            return copyAssign(lhs,newValues);
          },
          stddev:(data:DatedCoverage[]) => {
            return copyAssign(data[0],stats.std);
          },
          min:(data:DatedCoverage[])=>{
            throw new Error('Not implemented');
          },
          max:(data:DatedCoverage[])=>{
            throw new Error('Not implemented');
          },
          trend:(data:DatedCoverage[])=>{
            return copyAssign(data[0],stats.trend);
          }
        }).coverage;
      }));
  }

  private getSummaryStats(v: string, layer: FlattenedLayerDescriptor, timeperiod: TimePeriod): Observable<number[]> {
    if(layer.combine){
      return forkJoin(FeatureDataService.nestedLayers(layer).map(l=>this.getSummaryStats(v,l as any,timeperiod))).pipe(
        map(data=>[].concat(...data))
      );
    }

    const startMonth = timeperiod.from.getUTCMonth()+1;
    let durationInMonths = TimePeriodUtils.durationInMonths(timeperiod); 
    
    const url = `${layer.summary}/${v}_s${startMonth}_d${durationInMonths}.csv`;
    if(!this.statsCache[url]){
      const urlWithTime = url + '?t=' + new Date().getTime();
      this.statsCache[url] = this.http.get(urlWithTime,{responseType:'text'}).pipe(
        map(txt=>txt.split('\n').slice(1).filter(l=>l).map(l=>+l)),
        shareReplay());
    }

    return this.statsCache[url];
  }

  getTimeSeries(layer:FlattenedLayerDescriptor,feature:Feature,variable?:string):Observable<TimeSeries>{
    return of(layer).pipe(
      map(lyr=>this.featureData.getTimeSeries(lyr,feature,variable)),
      switchAll());
  }

  private _layerConfig(lbl:string): Observable<FeatureDataConfig>{
    return this.layers.pipe(
      map(cfg => {
        return cfg.find(lyr => lyr.label === lbl);
      }));
  }
}

function copyAssign(dc: DatedCoverage, values: number[]): any {
  const result = {...dc};
  result.coverage = {...dc.coverage};
  result.coverage.features = dc.coverage.features.map((f,i)=>{
    return {...f,properties:{...f.properties,value:values[i]}};
  });
  return result;
}

function computeLimitRanks(coverage: FeatureCollection<Point,any>, layer: FlattenedLayerDescriptor): void {
  if(!coverage?.features?.length){
    return;
  }
  const rankVariable = layer.rankVariable || 'value';
  if(!rankVariable){
    return;
  }
  const featuresCopy = coverage.features.slice();
  featuresCopy.sort((a,b)=>(a.properties[rankVariable]*a.properties.rankWeight||0)-(b.properties[rankVariable]*b.properties.rankWeight||0)).reverse();
  featuresCopy.forEach((f,idx)=>{
    f.properties.limitVariableRank = idx+1;
  });
}


function capData(coverage: FeatureCollection<Point, { [name: string]: any; }>, minVal: number, maxVal: number): void {
  coverage.features.forEach(f=>{
    if(f.properties.value===null){
      f.properties.value = NaN;
    }

    if((minVal!==undefined)&&(minVal!==null)&&(f.properties.value<minVal)){
      f.properties.value = minVal;
    }

    if((maxVal!==undefined)&&(maxVal!==null)&&f.properties.value>maxVal){
      f.properties.value = maxVal;
    }
  })
}

function configureFeatureUnits(coverage: FeatureCollection<Point, { [name: string]: any; }>, layer: FlattenedLayerDescriptor, dataMode: DataMode): void {
  const units = LEGEND_UNITS[dataMode]||layer.units;
  coverage.features.forEach(f=>{
    f.properties.units = units;
  });
}

function filterNulls(coverage: FeatureCollection<Point, { [name: string]: any; }>): FeatureCollection<Point, { [name: string]: any; }> {
  let result = {...coverage};
  result.features = result.features.filter(f=>(f.properties.value!==null)&&!isNaN(f.properties.value));
  return result;
}